import { api } from "./client";
import type {
  Request as UserListRequest,
  Response as UserListResponse,
  CurrentTeamUserListResponse,
  UserWithRoleListResponse,
  TeamListVO,
  TeamListRequest,
  TeamListResponse,
  TeamCreateRequest,
  Country,
  CountryGroup,
  UserInfoDetail,
  TrListRequest,
  PageAppUserListVO,
  NewCountryGroup,
} from "./types/member.js";
import { CountryTypeEnum } from "./types/member.js";

/**
 * 用户分页列表
 * GET /api/v1/user/list
 */
export function getUserList(
  params: UserListRequest
): Promise<UserListResponse> {
  return api.get<UserListResponse>("/user/list", { params });
}

/**
 * 用户分页列表（带角色）
 * GET /api/v1/user/list-with-role
 */
export function getUserListWithRole(
  params: UserListRequest
): Promise<UserWithRoleListResponse> {
  return api.get<UserWithRoleListResponse>("/user/list-with-role", { params });
}
/**
 * 用户分页列表
 * GET /api/v1/user/list-ifa
 */
export function getIfaUserList(
  params: UserListRequest
): Promise<UserListResponse> {
  return api.get<UserListResponse>("/user/list-ifa", { params });
}

/**
 * 当前团队用户分页列表
 * GET /api/v1/user/list-current-team
 */
export function getCurrentTeamUserList(
  params: UserListRequest
): Promise<CurrentTeamUserListResponse> {
  return api.get<CurrentTeamUserListResponse>("/user/list-current-team", { params });
}
/**
 * 新增/更新用户信息
 * POST /api/v1/user/save
 */
export function saveUser(
  data: import("./types/member").UserSaveRequest
): Promise<boolean> {
  return api.post<boolean>("/user/save", data);
}

/**
 * 获取团队列表
 * GET /api/v1/user/team/list
 */
export function getTeamList(): Promise<TeamListVO[]> {
  return api.get<TeamListVO[]>("/user/team/list");
}

/**
 * 获取团队分页列表
 * GET /api/v1/user/team/page
 */
export function getTeamPageList(
  params: TeamListRequest
): Promise<TeamListResponse> {
  return api.get<TeamListResponse>("/user/team/page", { params });
}

/**
 * 获取国家列表
 * GET /api/v1/basic/country/all
 */
export function getCountryList(name?: string): Promise<Country[]> {
  return api.get<Country[]>("/basic/country/all", { params: { name } });
}

/**
 * 获取国家分组列表
 * GET /api/v1/basic/country/group
 */
export function getCountryGroupList(): Promise<CountryGroup> {
  return api.get<CountryGroup>("/basic/country/group");
}

/**
 * 获取用户详细信息
 * GET /api/v1/user/info-detail
 */
export function getUserInfoDetail(uid: number): Promise<UserInfoDetail> {
  return api.get<UserInfoDetail>("/user/info-detail", { params: { uid } });
}

/**
 * 重置用户密码
 * POST /api/v1/user/reset-password/${uid}
 */
export function resetUserPassword(uid: number): Promise<boolean> {
  return api.post<boolean>(`/user/reset-password/${uid}`);
}

/**
 * 创建团队
 * POST /api/v1/user/team/create
 */
export function createTeam(data: TeamCreateRequest): Promise<boolean> {
  return api.post<boolean>("/user/team/create", data);
}

/**
 * 更新团队信息
 * POST /api/v1/user/team/update
 */
export function updateTeam(data: TeamCreateRequest): Promise<boolean> {
  return api.post<boolean>("/user/team/update", data);
}

/**
 * TR分页列表
 * GET /api/v1/user/tr-list
 */
export function getTrList(params: TrListRequest): Promise<PageAppUserListVO> {
  return api.get<PageAppUserListVO>("/user/tr-list", { params });
}

/**
 * 删除团队
 * POST /api/v1/user/team/delete/${teamId}
 */
export function deleteTeam(teamId: number): Promise<boolean> {
  return api.post<boolean>(`/user/team/delete/${teamId}`);
}

/**
 * 启用用户
 * POST /api/v1/user/enable/${uid}
 */
export function enableUser(uid: number): Promise<boolean> {
  return api.post<boolean>(`/user/enable/${uid}`);
}

/**
 * 禁用用户
 * POST /api/v1/user/disable/${uid}
 */
export function disableUser(uid: number): Promise<boolean> {
  return api.post<boolean>(`/user/disable/${uid}`);
}

/**
 * 获取国家列表（新）
 * GET /api/v1/basic/country/group-new
 */
export function getCountryListNew(
  type: CountryTypeEnum,
  keyword?: string
): Promise<NewCountryGroup> {
  return api.get<NewCountryGroup>("/basic/country/group-new", {
    params: { type, keyword },
  });
}

/**
 * 签署城市枚举
 */
export enum SignCity {
  HongKong = "HongKong",
  Other = "OTHER",
}

/**
 * 定位验证响应
 */
export interface LocateResponse {
  otherCity?: string;
  result?: boolean;
  signCity?: SignCity;
  [property: string]: any;
}

/**
 * 定位验证
 * POST /api/v1/basic/locate
 * @returns signCity为HongKong时可以签署，否则不允许签署
 */
export function locate(data: {
  latitude: number;
  longitude: number;
}): Promise<LocateResponse> {
  return api.post("/basic/locate", data);
}
